// ** React Imports
import { useContext, useEffect, useState } from "react"
import { useIntl } from "react-intl"
import { Button, Input, FormGroup, Label, Card, CardBody } from 'reactstrap'
import { AlertTriangle } from 'react-feather'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import { useReactOidc } from "@axa-fr/react-oidc-context"

// ** Custom Components
import Loading from "@views/common/Loading"
import Notification from "@views/common/Notification"

// ** Context
import { IntlContext } from "@src/utility/context/Internationalization"

// ** Third Party Components
import { changePwd } from '@services/PersonService'

const ChangePasswordEn = () => {
    const intl = useIntl()
    const { oidcUser, login } = useReactOidc()
    const intlContext = useContext(IntlContext)
    const headers = {
        Authorization: `Bearer ${oidcUser && oidcUser.access_token}`,
        "Accept-Language": intlContext.locale
    }

    const eye = <FontAwesomeIcon icon={faEye} />
    const eyeSlash = <FontAwesomeIcon icon={faEyeSlash} />

    const [success, setSuccess] = useState(null)
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(false)
    const [passwordOldShown, setPasswordOldShown] = useState(false)
    const [passwordNewShown, setPasswordNewShown] = useState(false)
    const [passwordConfirmShown, setPasswordConfirmShown] = useState(false)
    const [pwdOld, setPwdOld] = useState('')
    const [pwdNew, setPwdNew] = useState('')
    const [pwdConfirm, setPwdConfirm] = useState('')

    useEffect(() => {
        login()
    }, [])

    useEffect(() => {
        intlContext.switchLanguage("en")
    }, [intlContext])

    useEffect(() => {
        if (oidcUser) {
            login()
        }
    }, [oidcUser])

    const changePassword = () => {
        setLoading(true)
        changePwd(headers, {
            passwordOld: pwdOld,
            passwordNew: pwdNew,
            passwordConfirm: pwdConfirm
        })
            .then((res) => {
                setSuccess(res.data)
                window.parent.postMessage("success-change-password", "*")
            })
            .catch((err) => {
                setError(err)
            })
            .finally(() => {
                setSuccess(null)
                setError(null)
                setLoading(false)
            })
    }

    const changePwdValues = (values, label) => {
        if (label === 'pwdOld') {
            setPwdOld(values)
        } else if (label === 'pwdNew') {
            setPwdNew(values)
        } else {
            setPwdConfirm(values)
        }
    }

    const togglePasswordOldVisiblity = () => {
        setPasswordOldShown(!passwordOldShown)
    }

    const togglePasswordNewVisiblity = () => {
        setPasswordNewShown(!passwordNewShown)
    }

    const togglePasswordConfirmVisiblity = () => {
        setPasswordConfirmShown(!passwordConfirmShown)
    }

    return (
        <Card className="mb-0">
            {loading && <Loading />}
            {error && <Notification data={{ error }} />}
            {success && <Notification data={{ success }} />}
            <CardBody>
                <FormGroup>
                    <Label className="label" for="pwdOld">
                        {intl.formatMessage({ id: "pwdOld" })}
                        <span className="text-danger">*</span>
                    </Label>
                    <div className="pass-wrapper" style={{ position: "relative", display: "flex" }}>
                        <Input
                            style={{ margin: 0 }}
                            type={passwordOldShown ? "text" : "password"}
                            id="pwdOld"
                            name="pwdOld"
                            placeholder={`${intl.formatMessage({ id: "pwdOld" })}`}
                            onChange={(e) => changePwdValues(e.target.value, "pwdOld")}
                        />
                        {passwordOldShown ? <i onClick={togglePasswordOldVisiblity} style={{ position: "absolute", cursor: "pointer", right: 10, marginTop: 10 }}>{eyeSlash}</i> : <i onClick={togglePasswordOldVisiblity} style={{ position: "absolute", cursor: "pointer", right: 10, marginTop: 10 }}>{eye}</i>}
                    </div>
                </FormGroup>
                <FormGroup>
                    <Label className="label" for="pwdNew">
                        {intl.formatMessage({ id: "pwdNew" })}
                        <span style={{ color: 'red' }}>*</span>
                    </Label>
                    <div className="pass-wrapper" style={{ position: "relative", display: "flex" }}>
                        <Input
                            type={passwordNewShown ? "text" : "password"}
                            id="pwdNew"
                            name="pwdNew"
                            placeholder={`${intl.formatMessage({ id: "pwdNew" })}`}
                            onChange={(e) => changePwdValues(e.target.value, "pwdNew")}
                        />
                        {passwordNewShown ? <i onClick={togglePasswordNewVisiblity} style={{ position: "absolute", cursor: "pointer", right: 10, marginTop: 10 }}>{eyeSlash}</i> : <i onClick={togglePasswordNewVisiblity} style={{ position: "absolute", cursor: "pointer", right: 10, marginTop: 10 }}>{eye}</i>}
                    </div>
                </FormGroup>
                <FormGroup>
                    <Label className="label" for="pwdConfirm">
                        {intl.formatMessage({ id: "pwdConfirm" })}
                        <span style={{ color: 'red' }}>*</span>
                    </Label>
                    <div className="pass-wrapper" style={{ position: "relative", display: "flex" }}>
                        <Input
                            type={passwordConfirmShown ? "text" : "password"}
                            id="pwdConfirm"
                            name="pwdConfirm"
                            placeholder={`${intl.formatMessage({ id: "pwdConfirm" })}`}
                            onChange={(e) => changePwdValues(e.target.value, "pwdConfirm")}
                        />
                        {passwordConfirmShown ? <i onClick={togglePasswordConfirmVisiblity} style={{ position: "absolute", cursor: "pointer", right: 10, marginTop: 10 }}>{eyeSlash}</i> : <i onClick={togglePasswordConfirmVisiblity} style={{ position: "absolute", cursor: "pointer", right: 10, marginTop: 10 }}>{eye}</i>}
                    </div>
                </FormGroup>
                <AlertTriangle style={{ color: "#ff9f43" }} size={14} /> {intl.formatMessage({ id: "passwordPolicy" })}
                <div className="d-flex justify-content-center">
                    <Button className="mr-1" color='primary' onClick={changePassword}>
                        {intl.formatMessage({ id: "changePwd" })}
                    </Button>
                </div>
            </CardBody>

        </Card>
    )
}

export default ChangePasswordEn